import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Field, Modal, MoneyInput, PercentInput, useToast } from "@cx/ui";
import type { TitleCreated, TitleDetail, TitleFormOptions } from "@cx/contracts";
import {
  MAX_TITLE_OCCURRENCES,
  OCCURRENCES,
  formatBRL,
  todayInSaoPaulo,
  type Occurrence,
  type TitleSide,
} from "@cx/domain";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ContactPicker } from "./ContactPicker";
import { OCCURRENCE_LABEL, SIDE } from "./labels";

const WEEKDAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];

export function useTitleOptions(side: TitleSide, enabled = true) {
  const { companyId, api } = useSession();
  return useQuery({
    queryKey: ["titles", companyId, side, "form-options"],
    enabled,
    queryFn: () => api<TitleFormOptions>(`${SIDE[side].api}/form-options`),
  });
}

type TitleValues = {
  contact: { id: string; name: string } | null;
  amountCents: number | null;
  issueDate: string;
  competenceDate: string;
  dueDate: string;
  description: string;
  paymentMethodId: string | null;
  financialAccountId: string | null;
  bankNumber: string;
  documentNumber: string;
  financialCategoryId: string | null;
  sellerId: string | null;
  interestMonthPct4: number;
  finePct4: number;
  occurrence: Occurrence;
  count: number;
  weekday: number | null;
};

const fromDetail = (t: TitleDetail): TitleValues => ({
  contact: { id: t.contactId, name: t.contactName },
  amountCents: t.amountCents,
  issueDate: t.issueDate,
  competenceDate: t.competenceDate,
  dueDate: t.dueDate,
  description: t.description ?? "",
  paymentMethodId: t.paymentMethodId,
  financialAccountId: t.financialAccountId,
  bankNumber: t.bankNumber ?? "",
  documentNumber: t.documentNumber ?? "",
  financialCategoryId: t.financialCategoryId,
  sellerId: t.sellerId,
  interestMonthPct4: t.interestMonthPct4,
  finePct4: t.finePct4,
  occurrence: "single",
  count: 1,
  weekday: null,
});

const blank = (): TitleValues => {
  const today = todayInSaoPaulo();
  return {
    contact: null,
    amountCents: null,
    issueDate: today,
    competenceDate: today,
    dueDate: today,
    description: "",
    paymentMethodId: null,
    financialAccountId: null,
    bankNumber: "",
    documentNumber: "",
    financialCategoryId: null,
    sellerId: null,
    interestMonthPct4: 0,
    finePct4: 0,
    occurrence: "single",
    count: 1,
    weekday: null,
  };
};

/** Inclusão (com parcelas/recorrência) e edição de uma conta a receber ou a pagar. */
export function TitleDialog({
  side,
  title,
  onClose,
  onSaved,
}: {
  side: TitleSide;
  title?: TitleDetail;
  onClose(): void;
  onSaved(ids: string[]): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const options = useTitleOptions(side);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const [v, setV] = useState<TitleValues>(() => (title ? fromDetail(title) : blank()));
  const set = <K extends keyof TitleValues>(k: K, val: TitleValues[K]) => setV((x) => ({ ...x, [k]: val }));
  const s = SIDE[side];
  const o = options.data;
  const save = useMutation({
    mutationFn: async () => {
      const { contact, occurrence, count, weekday, ...rest } = v;
      const base = {
        ...rest,
        contactId: contact?.id ?? "",
        amountCents: v.amountCents ?? 0,
        description: v.description || null,
        bankNumber: v.bankNumber || null,
        documentNumber: v.documentNumber || null,
      };
      if (title) {
        await api(`${s.api}/${title.id}`, { method: "PUT", body: JSON.stringify(base) });
        return [title.id];
      }
      const r = await api<TitleCreated>(s.api, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({ ...base, occurrence, count, weekday }),
      });
      return r.ids;
    },
    onSuccess: (ids) => {
      toast(
        "success",
        title ? "Conta salva" : ids.length > 1 ? `${ids.length} contas incluídas` : "Conta incluída",
      );
      onSaved(ids);
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields)
        setErrors({ ...e.fields, ...(e.fields.contactId ? { contact: e.fields.contactId } : {}) });
      toast("danger", errorMessage(e));
    },
  });
  const pick = (
    k: "paymentMethodId" | "financialAccountId" | "financialCategoryId" | "sellerId",
    list: { id: string; label: string; active?: boolean }[],
  ) => (
    <select className="form-select" value={v[k] ?? ""} onChange={(e) => set(k, e.target.value || null)}>
      <option value="">Nenhum</option>
      {list
        .filter((x) => x.active !== false || x.id === v[k])
        .map((x) => (
          <option key={x.id} value={x.id}>
            {x.label}
          </option>
        ))}
    </select>
  );
  const date = (k: "issueDate" | "competenceDate" | "dueDate") => (
    <input type="date" value={v[k]} onChange={(e) => set(k, e.target.value)} />
  );
  return (
    <Modal
      open={open}
      title={title ? `Editar ${s.noun}` : `Nova ${s.noun}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar
          </button>
        </>
      }
    >
      <ContactPicker
        label={s.contact}
        value={v.contact}
        error={errors.contact}
        onChange={(c) => set("contact", c)}
      />
      <div className="row">
        <Field label="Valor" required className="col-md-4" error={errors.amountCents}>
          <MoneyInput value={v.amountCents} onChange={(c) => set("amountCents", c)} />
        </Field>
        <Field label="Vencimento" required className="col-md-4" error={errors.dueDate}>
          {date("dueDate")}
        </Field>
        <Field label="Emissão" required className="col-md-4" error={errors.issueDate}>
          {date("issueDate")}
        </Field>
        <Field label="Competência" required className="col-md-4" error={errors.competenceDate}>
          {date("competenceDate")}
        </Field>
        <Field label="Nº documento" className="col-md-4" error={errors.documentNumber}>
          <input
            value={v.documentNumber}
            maxLength={30}
            onChange={(e) => set("documentNumber", e.target.value)}
          />
        </Field>
        <Field label="Nº no banco" className="col-md-4" error={errors.bankNumber}>
          <input value={v.bankNumber} maxLength={30} onChange={(e) => set("bankNumber", e.target.value)} />
        </Field>
        <Field label="Histórico" className="col-12" error={errors.description}>
          <input
            value={v.description}
            maxLength={2000}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>
        {o && (
          <>
            <Field label="Categoria" className="col-md-6" error={errors.financialCategoryId}>
              {pick(
                "financialCategoryId",
                o.categories.map((c) => ({ id: c.id, label: c.path, active: c.active })),
              )}
            </Field>
            <Field label="Forma de pagamento" className="col-md-6" error={errors.paymentMethodId}>
              {pick(
                "paymentMethodId",
                o.paymentMethods.map((m) => ({ id: m.id, label: m.name, active: m.active })),
              )}
            </Field>
            <Field label="Conta prevista" className="col-md-6" error={errors.financialAccountId}>
              {pick(
                "financialAccountId",
                o.accounts.map((a) => ({ id: a.id, label: a.name, active: a.active })),
              )}
            </Field>
            {side === "R" && !o.sellerLocked && (
              <Field label="Vendedor" className="col-md-6" error={errors.sellerId}>
                {pick(
                  "sellerId",
                  o.sellers.map((x) => ({ id: x.id, label: x.name })),
                )}
              </Field>
            )}
          </>
        )}
        <Field label="Juros ao mês" className="col-md-3" error={errors.interestMonthPct4}>
          <PercentInput value={v.interestMonthPct4} onChange={(p) => set("interestMonthPct4", p ?? 0)} />
        </Field>
        <Field label="Multa" className="col-md-3" error={errors.finePct4}>
          <PercentInput value={v.finePct4} onChange={(p) => set("finePct4", p ?? 0)} />
        </Field>
        {!title && (
          <>
            <Field label="Ocorrência" className="col-md-3" error={errors.occurrence}>
              <select
                className="form-select"
                value={v.occurrence}
                onChange={(e) => {
                  const occ = e.target.value as Occurrence;
                  setV((x) => ({
                    ...x,
                    occurrence: occ,
                    count: occ === "single" ? 1 : x.count > 1 ? x.count : occ === "installment" ? 2 : 12,
                    weekday: occ === "weekly" ? (x.weekday ?? 1) : null,
                  }));
                }}
              >
                {OCCURRENCES.map((occ) => (
                  <option key={occ} value={occ}>
                    {OCCURRENCE_LABEL[occ]}
                  </option>
                ))}
              </select>
            </Field>
            {v.occurrence !== "single" && (
              <Field
                label={v.occurrence === "installment" ? "Parcelas" : "Ocorrências"}
                className="col-md-3"
                error={errors.count}
                hint={
                  v.occurrence === "installment" && v.amountCents
                    ? `Total dividido: ~${formatBRL(Math.floor(v.amountCents / Math.max(v.count, 1)))} cada`
                    : undefined
                }
              >
                <input
                  type="number"
                  min={2}
                  max={MAX_TITLE_OCCURRENCES}
                  value={v.count}
                  onChange={(e) => set("count", Number(e.target.value) || 0)}
                />
              </Field>
            )}
            {v.occurrence === "weekly" && (
              <Field label="Dia da semana" className="col-md-3" error={errors.weekday}>
                <select
                  className="form-select"
                  value={v.weekday ?? 1}
                  onChange={(e) => set("weekday", Number(e.target.value))}
                >
                  {WEEKDAYS.map((d, i) => (
                    <option key={d} value={i}>
                      {d}
                    </option>
                  ))}
                </select>
              </Field>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Baixa (recebimento ou pagamento) parcial ou total. Juros, multa e taxa vazios = calculados pelo servidor. Desconto
 * só com `canDiscount` (ação settle_discount do módulo); o servidor recusa também a baixa sem dinheiro na conta.
 */
export function SettleDialog({
  side,
  title,
  canDiscount,
  onClose,
  onSaved,
}: {
  side: TitleSide;
  title: TitleDetail;
  canDiscount: boolean;
  onClose(): void;
  onSaved(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const options = useTitleOptions(side);
  const [open, setOpen] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [key] = useState(() => crypto.randomUUID());
  const s = SIDE[side];
  const method = options.data?.paymentMethods.find((m) => m.id === title.paymentMethodId);
  const [v, setV] = useState({
    paidAt: todayInSaoPaulo(),
    amountCents: title.openCents as number | null,
    discountCents: 0,
    interestCents: null as number | null,
    fineCents: null as number | null,
    feeCents: null as number | null,
    financialAccountId: title.financialAccountId ?? "",
    paymentMethodId: title.paymentMethodId,
    notes: "",
  });
  const account = v.financialAccountId || method?.defaultAccountId || "";
  const save = useMutation({
    mutationFn: () =>
      api(`${s.api}/${title.id}/settle`, {
        method: "POST",
        headers: { "Idempotency-Key": key },
        body: JSON.stringify({
          ...v,
          financialAccountId: account,
          amountCents: v.amountCents ?? 0,
          notes: v.notes || null,
        }),
      }),
    onSuccess: () => {
      toast("success", side === "R" ? "Recebimento registrado" : "Pagamento registrado");
      onSaved();
      setOpen(false);
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "amount_exceeds_balance")
        setErrors({ amountCents: e.message });
      else if (e instanceof ApiFailure && (e.code === "future_date" || e.code === "date_before_opening"))
        setErrors({ paidAt: errorMessage(e) });
      toast("danger", errorMessage(e));
    },
  });
  const c = title.chargesToday;
  return (
    <Modal
      open={open}
      title={side === "R" ? "Registrar recebimento" : "Registrar pagamento"}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => save.mutate()}
          >
            Confirmar
          </button>
        </>
      }
    >
      <p className="mb-2">
        Saldo em aberto: <strong>{formatBRL(title.openCents)}</strong>
        {c.daysLate > 0 && (
          <span className="text-danger">
            {" "}
            · {c.daysLate} {c.daysLate === 1 ? "dia" : "dias"} de atraso (juros {formatBRL(c.interestCents)},
            multa {formatBRL(c.fineCents)} sobre o saldo)
          </span>
        )}
      </p>
      <div className="row">
        <Field
          label={side === "R" ? "Data do recebimento" : "Data do pagamento"}
          required
          className="col-md-6"
          error={errors.paidAt}
        >
          <input
            type="date"
            value={v.paidAt}
            max={todayInSaoPaulo()}
            onChange={(e) => setV({ ...v, paidAt: e.target.value })}
          />
        </Field>
        <Field
          label="Valor da baixa"
          required
          className="col-md-6"
          error={errors.amountCents}
          hint="Parte do saldo quitada"
        >
          <MoneyInput value={v.amountCents} onChange={(x) => setV({ ...v, amountCents: x })} />
        </Field>
        <Field label="Conta" required className="col-md-6" error={errors.financialAccountId}>
          <select
            className="form-select"
            value={account}
            onChange={(e) => setV({ ...v, financialAccountId: e.target.value })}
          >
            <option value="">Selecione</option>
            {options.data?.accounts
              .filter((a) => a.active)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Forma de pagamento" className="col-md-6" error={errors.paymentMethodId}>
          <select
            className="form-select"
            value={v.paymentMethodId ?? ""}
            onChange={(e) => setV({ ...v, paymentMethodId: e.target.value || null })}
          >
            <option value="">Nenhuma</option>
            {options.data?.paymentMethods
              .filter((m) => m.active || m.id === v.paymentMethodId)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
        </Field>
        <Field
          label="Desconto"
          className="col-md-3"
          error={errors.discountCents}
          hint={canDiscount ? undefined : "Exige permissão"}
        >
          <MoneyInput
            value={v.discountCents}
            disabled={!canDiscount}
            onChange={(x) => setV({ ...v, discountCents: x ?? 0 })}
          />
        </Field>
        <Field label="Juros" className="col-md-3" error={errors.interestCents} hint="Vazio = calculado">
          <MoneyInput value={v.interestCents} onChange={(x) => setV({ ...v, interestCents: x })} />
        </Field>
        <Field label="Multa" className="col-md-3" error={errors.fineCents} hint="Vazio = calculada">
          <MoneyInput value={v.fineCents} onChange={(x) => setV({ ...v, fineCents: x })} />
        </Field>
        <Field label="Taxa" className="col-md-3" error={errors.feeCents} hint="Vazio = da forma">
          <MoneyInput value={v.feeCents} onChange={(x) => setV({ ...v, feeCents: x })} />
        </Field>
        <Field label="Observações" className="col-12" error={errors.notes}>
          <input value={v.notes} maxLength={500} onChange={(e) => setV({ ...v, notes: e.target.value })} />
        </Field>
      </div>
    </Modal>
  );
}
